<nb-card class="global-product-dialog" style="min-width: 700px; max-width: 900px; max-height: 90vh; overflow-y: auto;">
  <nb-card-header>
    <h4>{{ product ? 'Edit Global Product' : (chainId ? 'Add Chain Product' : 'Add Global Product') }}</h4>
  </nb-card-header>

  <nb-card-body>
    <form [formGroup]="form">

      <!-- Fill from catalog template -->
      <div class="card border-0 shadow-sm mb-3 catalog-template-card">
        <div class="card-header py-2 px-3" style="background: #f8f9fa;">
          <span class="d-flex align-items-center gap-2">
            <i class="fas fa-file-import"></i>
            <strong>Fill from catalog template</strong>
          </span>
        </div>
        <div class="card-body">
          <p class="text-muted small mb-2">
            Search an existing catalog product to copy its fields into this form. Images are not copied.
          </p>
          <div class="global-search-container">
            <div class="input-group mb-2">
              <input type="text" class="form-control" placeholder="Search by name or barcode…"
                [(ngModel)]="catalogSearchTerm" [ngModelOptions]="{standalone: true}"
                (ngModelChange)="onCatalogSearchInput($event)">
              <span class="input-group-text">
                <span *ngIf="!catalogSearchLoading && !catalogTemplateLoading"><i class="fas fa-search"></i></span>
                <span *ngIf="catalogSearchLoading || catalogTemplateLoading" class="spinner-border spinner-border-sm" role="status"></span>
              </span>
            </div>
            <div class="global-search-results" *ngIf="catalogSearchResults.length > 0">
              <div class="global-search-result-item"
                *ngFor="let gp of catalogSearchResults"
                (click)="selectCatalogTemplate(gp)">
                <strong>{{ gp.name }}</strong>
                <span *ngIf="gp.barcode" class="text-muted ms-2 small">EAN: {{ gp.barcode }}</span>
              </div>
            </div>
          </div>
          <p class="text-muted small mb-0"
            *ngIf="catalogSearchDone && catalogSearchResults.length === 0 && catalogSearchTerm.trim().length >= 2 && !catalogSearchLoading">
            No matches
          </p>
        </div>
      </div>

      <!-- BASIC INFORMATION -->
      <h6 class="section-title">Basic Information</h6>
      <div class="row">
        <div class="col-md-8 mb-3">
          <label class="form-label">Name <span class="text-danger">*</span></label>
          <input class="form-control" formControlName="name" placeholder="Product name"
            [class.is-invalid]="form.get('name')?.invalid && form.get('name')?.touched">
          <div class="invalid-feedback">Name is required</div>
        </div>
        <div class="col-md-4 mb-3">
          <label class="form-label">Barcode / EAN</label>
          <input class="form-control" formControlName="barcode" placeholder="EAN-13 or short code"
            (input)="onBarcodeInput()"
            [class.is-invalid]="form.get('barcode')?.invalid && form.get('barcode')?.touched">
          <div *ngIf="form.get('barcode')?.invalid && form.get('barcode')?.touched" class="text-danger small mt-1">
            {{ form.get('barcode')?.errors?.['invalidBarcode']?.message }}
          </div>
          <small class="text-muted">EAN-13 or short internal code (up to 12 digits)</small>
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">SKU</label>
          <input class="form-control" formControlName="sku" placeholder="Internal SKU">
        </div>
        <div class="col-12 mb-3">
          <label class="form-label">Description</label>
          <textarea class="form-control" formControlName="description" rows="3" placeholder="Product description"></textarea>
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">Global category</label>
          <select class="form-select" formControlName="globalCategoryId">
            <option value="">— None —</option>
            <option *ngFor="let cat of globalCategories" [value]="cat._id">{{ globalCategoryOptionLabel(cat) }}</option>
          </select>
          <small class="text-muted" *ngIf="globalCategoriesLoading">Loading categories…</small>
        </div>
      </div>

      <!-- IMAGES -->
      <h6 class="section-title">Images</h6>
      <div class="row mb-3">
        <div class="col-12">
          <input type="file" class="form-control mb-2" multiple accept="image/*" (change)="onImagesSelect($event)">
          <div class="image-previews" *ngIf="productImagePreviews.length > 0">
            <div class="row g-2">
              <div class="col-auto" *ngFor="let preview of productImagePreviews; let i = index">
                <div class="image-preview-container" [class.primary]="i === primaryImageIndex">
                  <img [src]="preview" alt="Product image" (error)="onImageError($event)">
                  <div class="image-overlay">
                    <button type="button" class="btn btn-sm btn-light" (click)="setPrimaryImage(i)"
                      [class.btn-warning]="i !== primaryImageIndex"
                      [class.btn-success]="i === primaryImageIndex"
                      title="Set as primary">
                      <i class="fa fa-star"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-danger" (click)="removeImage(i)"
                      title="Remove">
                      <i class="fa fa-trash"></i>
                    </button>
                    <span class="badge bg-warning text-dark" *ngIf="i === primaryImageIndex">Primary</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CLASSIFICATION -->
      <h6 class="section-title">Classification</h6>
      <div class="mb-3">
        <label class="form-label">Search DSK classification</label>
        <ng-select
          [items]="dskSearchResults"
          bindLabel="mainGroup"
          placeholder="Type to search DSK groups…"
          [typeahead]="dskSearchInput$"
          [loading]="dskSearchLoading"
          [(ngModel)]="selectedDskSearch"
          [ngModelOptions]="{ standalone: true }"
          (change)="onDskSearchSelected($event)"
          [clearable]="true"
          [minTermLength]="1">
          <ng-template ng-option-tmp let-item="item">
            <div>{{ formatDskSearchOptionLabel(item) }}</div>
            <small class="text-muted" *ngIf="formatDskSearchOptionMeta(item)">{{ formatDskSearchOptionMeta(item) }}</small>
          </ng-template>
        </ng-select>
        <small class="text-muted">Select a row to fill all classification fields, or edit them manually below.</small>
      </div>
      <div class="row mb-3">
        <div class="col-md-4 mb-2">
          <label class="form-label">Main Group</label>
          <input class="form-control" formControlName="mainGroup" placeholder="DSK main group code">
        </div>
        <div class="col-md-4 mb-2">
          <label class="form-label">Product Group</label>
          <input class="form-control" formControlName="productGroup" placeholder="DSK product group code">
        </div>
        <div class="col-md-4 mb-2">
          <label class="form-label">Sub Group</label>
          <input class="form-control" formControlName="subGroup" placeholder="DSK sub-group code">
        </div>
        <div class="col-md-4 mb-2">
          <label class="form-label">DSK Assortment ID</label>
          <input class="form-control" formControlName="dskAssortmentID" placeholder="DSK assortment ID">
        </div>
        <div class="col-md-4 mb-2">
          <label class="form-label">Family</label>
          <input class="form-control" formControlName="family" placeholder="Internal family mapping">
        </div>
        <div class="col-md-4 mb-2">
          <label class="form-label">Discount Group</label>
          <input class="form-control" formControlName="discountGroup" placeholder="Campaign/discount group">
        </div>
      </div>

      <h6 class="section-title">Default Store Pricing</h6>
      <div class="row mb-3">
        <div class="col-md-6 mb-2">
          <label class="form-label">Default cost price (DKK)</label>
          <div class="input-group price-input-group">
            <input type="number" class="form-control" formControlName="defaultCostPrice" min="0" step="0.01" placeholder="Optional">
            <span class="input-group-text">DKK</span>
          </div>
        </div>
        <div class="col-md-6 mb-2">
          <label class="form-label">Default sell price (DKK)</label>
          <div class="input-group price-input-group">
            <input type="number" class="form-control" formControlName="defaultSellPrice" min="0" step="0.01" placeholder="Optional">
            <span class="input-group-text">DKK</span>
          </div>
        </div>
        <div class="col-12">
          <small class="text-muted">Applied to linked store products on link and on save, unless the store has locked Price / Cost price.</small>
        </div>
      </div>

      <!-- PHYSICAL PROPERTIES -->
      <h6 class="section-title">Physical Properties</h6>
      <div class="row mb-3">
        <div class="col-md-3 mb-2">
          <label class="form-label">Weight (g)</label>
          <input type="number" class="form-control" formControlName="weight" placeholder="0">
        </div>
        <div class="col-md-3 mb-2">
          <label class="form-label">Length (mm)</label>
          <input type="number" class="form-control" formControlName="length" placeholder="0">
        </div>
        <div class="col-md-3 mb-2">
          <label class="form-label">Width (mm)</label>
          <input type="number" class="form-control" formControlName="width" placeholder="0">
        </div>
        <div class="col-md-3 mb-2">
          <label class="form-label">Height (mm)</label>
          <input type="number" class="form-control" formControlName="height" placeholder="0">
        </div>
      </div>

      <!-- UNIT PRICING -->
      <h6 class="section-title">Unit Pricing</h6>
      <div class="row mb-3 unit-pricing-row">
        <div class="col-md-4 mb-2">
          <label class="form-label">Unit Code (Enhedskode)</label>
          <select class="form-select" formControlName="unitCode">
            <option value="">— Select —</option>
            <option *ngFor="let code of unitCodeOptions" [value]="code">{{ code }}</option>
          </select>
        </div>
        <div class="col-md-4 mb-2">
          <label class="form-label">Unit Content (Indhold pr. enhed)</label>
          <input type="number" class="form-control" formControlName="unitContent" min="0" step="any" placeholder="0"
            [class.is-invalid]="form.get('unitContent')?.invalid && form.get('unitContent')?.touched">
          <div class="unit-pricing-validation-slot">
            <div class="invalid-feedback d-block"
              *ngIf="form.get('unitContent')?.errors?.['unitContentZero'] && form.get('unitContent')?.touched">
              {{ form.get('unitContent')?.errors?.['unitContentZero']?.message }}
            </div>
          </div>
        </div>
      </div>

      <!-- INGREDIENTS & ALLERGENS -->
      <ng-container *ngIf="showAllergens">
      <h6 class="section-title">Ingredients &amp; Allergens</h6>
      <div class="row mb-3">
        <div class="col-12 mb-3">
          <label class="form-label">Ingredients</label>
          <textarea class="form-control ingredients-textarea" formControlName="ingredients" rows="5"
            placeholder="Paste full ingredient list from supplier packaging"></textarea>
          <div class="ingredients-meta-row">
            <small class="text-muted">{{ ingredientsCharCount }} / {{ ingredientsMaxLength }} characters</small>
            <small class="text-warning ingredients-warn"
              *ngIf="ingredientsCharCount >= ingredientsSoftWarnLength && ingredientsCharCount <= ingredientsMaxLength">
              Approaching maximum length — backend will reject over {{ ingredientsMaxLength }} characters
            </small>
            <small class="text-danger ingredients-warn"
              *ngIf="ingredientsCharCount > ingredientsMaxLength">
              Exceeds maximum length of {{ ingredientsMaxLength }} characters
            </small>
          </div>
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">Allergens</label>
          <div class="allergen-checkbox-grid">
            <div class="form-check" *ngFor="let allergen of allergenOptions">
              <input class="form-check-input" type="checkbox"
                [id]="'global-allergen-' + allergen.value"
                [checked]="isAllergenSelected('allergens', allergen.value)"
                (change)="toggleAllergen('allergens', allergen.value, $any($event.target).checked)">
              <label class="form-check-label" [for]="'global-allergen-' + allergen.value">{{ allergen.label }}</label>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">May contain traces of</label>
          <div class="allergen-checkbox-grid">
            <div class="form-check" *ngFor="let allergen of allergenOptions">
              <input class="form-check-input" type="checkbox"
                [id]="'global-trace-' + allergen.value"
                [checked]="isAllergenSelected('allergenTraces', allergen.value)"
                (change)="toggleAllergen('allergenTraces', allergen.value, $any($event.target).checked)">
              <label class="form-check-label" [for]="'global-trace-' + allergen.value">{{ allergen.label }}</label>
            </div>
          </div>
        </div>
      </div>
      </ng-container>

      <!-- NUTRITION FACTS -->
      <ng-container *ngIf="showNutrition">
      <h6 class="section-title">Nutrition Facts</h6>
      <div class="row mb-3 nutrition-grid" formGroupName="nutrition">
        <div class="col-md-4 mb-2" *ngFor="let field of nutritionFields">
          <label class="form-label">{{ field.label }}</label>
          <div class="input-group">
            <input type="number" class="form-control" [formControlName]="field.key"
              min="0" step="any" inputmode="decimal" placeholder="0"
              [class.is-invalid]="form.get('nutrition.' + field.key)?.invalid && form.get('nutrition.' + field.key)?.touched">
            <span class="input-group-text">{{ field.unit }}</span>
          </div>
          <div class="nutrition-validation-slot">
            <div class="invalid-feedback d-block"
              *ngIf="form.get('nutrition.' + field.key)?.errors?.['nonNegative'] && form.get('nutrition.' + field.key)?.touched">
              {{ form.get('nutrition.' + field.key)?.errors?.['nonNegative']?.message }}
            </div>
          </div>
        </div>
      </div>
      </ng-container>

      <!-- VENDOR & ORDER DETAILS -->
      <h6 class="section-title">Vendor &amp; Order Details</h6>
      <div class="mb-3">
        <label class="form-label">Search vendor</label>
        <ng-select
          [items]="vendorSearchResults"
          bindLabel="vendorName"
          placeholder="Type vendor ID or name…"
          [typeahead]="vendorSearchInput$"
          [loading]="vendorSearchLoading"
          [(ngModel)]="selectedVendorSearch"
          [ngModelOptions]="{ standalone: true }"
          (change)="onVendorSearchSelected($event)"
          [clearable]="true"
          [minTermLength]="1">
          <ng-template ng-option-tmp let-item="item">
            <div>{{ item.vendorName || '—' }}</div>
            <small class="text-muted" *ngIf="item.vendorID">ID: {{ item.vendorID }}</small>
          </ng-template>
        </ng-select>
      </div>
      <div class="row mb-3 vendor-order-grid">
        <div class="col-md-4 mb-2">
          <label class="form-label">Vendor ID</label>
          <input class="form-control" formControlName="vendorID" placeholder="Supplier vendor ID">
        </div>
        <div class="col-md-4 mb-2">
          <label class="form-label">Vendor Name</label>
          <input class="form-control" formControlName="vendorName" placeholder="Supplier name">
        </div>
        <div class="col-md-4 mb-2">
          <label class="form-label">Order Number (vendor's article number)</label>
          <input class="form-control" formControlName="orderNumber" placeholder="Vendor article number">
        </div>
        <div class="col-md-4 mb-2">
          <label class="form-label">Producer / Manufacturer</label>
          <input class="form-control" formControlName="producer" placeholder="Producer or manufacturer">
        </div>
        <div class="col-md-4 mb-2">
          <label class="form-label">Country of Origin</label>
          <input class="form-control" formControlName="country" placeholder="e.g. Denmark">
        </div>
        <div class="col-md-2 mb-2">
          <label class="form-label">Coli (units per case)</label>
          <input type="number" class="form-control" formControlName="coli" min="1" step="1" inputmode="numeric" placeholder="1"
            [class.is-invalid]="form.get('coli')?.invalid && form.get('coli')?.touched">
          <div class="vendor-order-validation-slot">
            <div class="invalid-feedback d-block"
              *ngIf="form.get('coli')?.errors?.['integerMin'] && form.get('coli')?.touched">
              {{ form.get('coli')?.errors?.['integerMin']?.message }}
            </div>
          </div>
        </div>
        <div class="col-md-2 mb-2">
          <label class="form-label">Shelf Life (days)</label>
          <input type="number" class="form-control" formControlName="shelfLifeDays" min="0" step="1" inputmode="numeric" placeholder="0"
            [class.is-invalid]="form.get('shelfLifeDays')?.invalid && form.get('shelfLifeDays')?.touched">
          <div class="vendor-order-validation-slot">
            <div class="invalid-feedback d-block"
              *ngIf="form.get('shelfLifeDays')?.errors?.['integerMin'] && form.get('shelfLifeDays')?.touched">
              {{ form.get('shelfLifeDays')?.errors?.['integerMin']?.message }}
            </div>
          </div>
        </div>
      </div>

      <!-- RESTRICTIONS -->
      <h6 class="section-title">Restrictions</h6>
      <div class="row mb-3">
        <div class="col-md-6 mb-2">
          <div class="form-check form-switch mt-2">
            <input class="form-check-input" type="checkbox" formControlName="ageRestricted" id="ageRestrictedCheck">
            <label class="form-check-label" for="ageRestrictedCheck">Age Restricted</label>
          </div>
        </div>
        <div class="col-md-6 mb-2" *ngIf="ageRestricted">
          <label class="form-label">Minimum Age <span class="text-danger">*</span></label>
          <select class="form-select" formControlName="minimumAge"
            [class.is-invalid]="form.get('minimumAge')?.invalid && form.get('minimumAge')?.touched">
            <option [ngValue]="null" disabled>-- Select --</option>
            <option [ngValue]="16">16</option>
            <option [ngValue]="18">18</option>
          </select>
          <div class="invalid-feedback">Minimum age is required</div>
        </div>
      </div>

      <!-- DEPOSIT -->
      <h6 class="section-title">Deposit</h6>
      <div class="row mb-3">
        <div class="col-12 mb-2">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" formControlName="hasDeposit" id="hasDepositCheck">
            <label class="form-check-label" for="hasDepositCheck">Has Deposit</label>
          </div>
        </div>
        <ng-container *ngIf="hasDeposit">
          <div class="col-md-6 mb-2" *ngIf="depositFeeTypes.length > 0">
            <label class="form-label">Deposit Type <span class="text-danger">*</span></label>
            <select class="form-select" formControlName="depositFeeTypeId"
              [class.is-invalid]="form.get('depositFeeTypeId')?.invalid && form.get('depositFeeTypeId')?.touched">
              <option value="">-- Select deposit type --</option>
              <option *ngFor="let ft of depositFeeTypes" [value]="ft._id">
                {{ ft.name }}{{ ft.description ? ' — ' + ft.description : '' }}
                ({{ ft.defaultAmount | number:'1.2-2' }} DKK)
              </option>
            </select>
            <div class="invalid-feedback">Deposit type is required</div>
          </div>
          <div class="col-md-6 mb-2" *ngIf="depositFeeTypes.length === 0">
            <label class="form-label">Deposit Category <span class="text-danger">*</span></label>
            <select class="form-select" formControlName="depositCategory"
              [class.is-invalid]="form.get('depositCategory')?.invalid && form.get('depositCategory')?.touched">
              <option value="">-- Select category --</option>
              <option value="A">A — 1.00 DKK</option>
              <option value="B">B — 1.50 DKK</option>
              <option value="C">C — 3.00 DKK</option>
            </select>
            <div class="invalid-feedback">Deposit category is required</div>
          </div>
          <div class="col-md-6 mb-2">
            <label class="form-label">Provider Deposit Code</label>
            <input class="form-control" formControlName="providerDepositCode" placeholder="e.g. PANT_A">
          </div>
        </ng-container>
      </div>

      <!-- TAGS -->
      <h6 class="section-title">Tags</h6>
      <div class="row mb-3">
        <div class="col-12">
          <input class="form-control" formControlName="tagsInput" placeholder="dairy, organic, gluten-free">
          <small class="text-muted">Separate multiple tags with commas</small>
        </div>
      </div>

      <!-- SEO / META -->
      <h6 class="section-title">SEO / Meta</h6>
      <div class="row mb-3">
        <div class="col-md-6 mb-2">
          <label class="form-label">Meta Title</label>
          <input class="form-control" formControlName="metaTitle" placeholder="SEO title">
        </div>
        <div class="col-12 mb-2">
          <label class="form-label">Meta Description</label>
          <textarea class="form-control" formControlName="metaDescription" rows="2" placeholder="SEO description"></textarea>
        </div>
      </div>

    </form>
  </nb-card-body>

  <nb-card-footer class="d-flex justify-content-end gap-2">
    <button class="btn btn-outline-secondary" type="button" (click)="cancel()" [disabled]="saving">Cancel</button>
    <button class="btn btn-primary" type="button" (click)="save()" [disabled]="form.invalid || saving">
      <span *ngIf="saving" class="spinner-border spinner-border-sm me-1" role="status"></span>
      {{ saving ? 'Saving…' : (product ? 'Update Product' : 'Add Product') }}
    </button>
  </nb-card-footer>
</nb-card>
